import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { RETENTION_DAYS } from "@/config/retention";
import { useUserStore } from "@/stores/user-store";

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteAccountModalProps) {
  const { isWhale } = useUserStore();
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, isDeleting, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={isDeleting ? undefined : onClose}
      />

      <div className="relative w-[380px] rounded-2xl border border-white/10 shadow-2xl shadow-black/40 p-6 animate-modal-in"
        style={{ background: "linear-gradient(180deg, #2a2035 0%, #1a1520 100%)" }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="size-12 rounded-full bg-red-500/15 flex items-center justify-center mb-4">
            <AlertTriangle className="size-6 text-red-400" />
          </div>

          <h2 className="text-lg font-bold text-foreground mb-2">
            Delete your account?
          </h2>

          <p className="text-[13px] text-muted-foreground leading-relaxed mb-6">
            This will permanently delete your account and sign you out.
            Your generated content will no longer be accessible
            {isWhale ? "." : ` and is deleted automatically ${RETENTION_DAYS} days after creation.`}
          </p>

          <div className="flex gap-3 w-full">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold btn-glass disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-70 transition-colors flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete my account"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
